/**
 * SSN Formatter
 * 
 * Handles progressive formatting of Social Security Numbers in XXX-XX-XXXX format
 * with secure masking functionality to protect sensitive data after blur events.
 */

import { BaseFormatter, FormatContext, ExtendedFormattingResult } from './base-formatter';
import { FormattingOptions, ValidationResult } from '../types/formatting-types';

/**
 * SSN formatting options
 */
interface SSNFormattingOptions extends FormattingOptions {
  /** Whether to mask after blur (show only last 4 digits) */
  maskAfterBlur?: boolean;
  /** Custom mask character (default: 'X') */
  maskCharacter?: string;
  /** Whether to show full SSN during editing */
  showFullDuringEdit?: boolean;
  /** Custom masked format (default: 'XXX-XX-1234') */
  maskedFormat?: string;
}

/**
 * SSN formatter implementing XXX-XX-XXXX format with secure masking
 * 
 * Features:
 * - Progressive formatting with automatic dash insertion
 * - Secure masking (XXX-XX-1234) after blur
 * - Input validation (numeric only, 9 digits)
 * - Security-focused design (no DOM storage of full SSN when masked)
 * - Real-time formatting during typing
 */
export class SSNFormatter extends BaseFormatter {
  private readonly maxLength = 9; // 9 digits total
  private readonly maskCharacter = 'X';
  
  // Internal storage for managing masked state
  private maskedValues = new WeakMap<HTMLElement, string>();
  private originalValues = new WeakMap<HTMLElement, string>();

  constructor() {
    super('ssn', {
      maskAfterBlur: true,
      maskCharacter: 'X',
      showFullDuringEdit: true,
      maskedFormat: 'XXX-XX-{last4}'
    });
  }

  /**
   * Extract raw digits from formatted SSN
   */
  public extractRaw(formattedValue: string): string {
    // If this is a masked value, we need to handle it carefully
    if (this.isMaskedValue(formattedValue)) {
      // For masked values, we can only return the last 4 digits
      // The full value should be retrieved from secure storage if needed
      return this.extractLast4FromMasked(formattedValue);
    }
    
    return this.stripFormatting(formattedValue);
  }

  /**
   * Get the full raw value for an element (if stored securely)
   */
  public getFullRawValue(element: HTMLElement): string | null {
    return this.originalValues.get(element) || null;
  }

  /**
   * Check if this formatter handles SSN fields
   */
  public canHandle(fieldType: string): boolean {
    return fieldType === 'ssn' || 
           fieldType === 'social_security' ||
           fieldType === 'social_security_number' ||
           fieldType === 'tax_id';
  }

  /**
   * Get the expected format pattern
   */
  public getFormatPattern(): string {
    return 'XXX-XX-XXXX';
  }

  /**
   * Sanitize input to only allow numeric characters
   */
  protected sanitizeInput(value: string): string {
    // Remove all non-numeric characters
    return value.replace(/\D/g, '');
  }

  /**
   * Perform progressive SSN formatting
   */
  protected performFormat(context: FormatContext): ExtendedFormattingResult {
    const { rawValue, cursorPosition, isProgressive, options } = context;
    const opts = options as SSNFormattingOptions;
    
    // Limit to 9 digits for SSN
    const digits = rawValue.substring(0, this.maxLength);
    
    let formattedValue = '';
    let newCursorPosition = cursorPosition;

    // Progressive formatting based on digit count
    if (digits.length === 0) {
      formattedValue = '';
    } else if (digits.length <= 3) {
      // First 3 digits: XXX
      formattedValue = digits;
    } else if (digits.length <= 5) {
      // First 5 digits: XXX-XX
      formattedValue = `${digits.substring(0, 3)}-${digits.substring(3)}`;
    } else {
      // Full format: XXX-XX-XXXX
      formattedValue = `${digits.substring(0, 3)}-${digits.substring(3, 5)}-${digits.substring(5)}`;
    }

    // Calculate cursor position for progressive formatting
    if (isProgressive && cursorPosition !== undefined) {
      newCursorPosition = this.calculateSSNCursorPosition(
        rawValue,
        digits,
        formattedValue,
        cursorPosition
      );
    }

    return {
      formattedValue,
      rawValue: digits,
      success: true,
      errors: [],
      newCursorPosition,
      wasModified: rawValue !== formattedValue
    };
  }

  /**
   * Apply masking to SSN (show only last 4 digits)
   */
  public applyMasking(
    fullSSN: string, 
    element?: HTMLElement, 
    options?: SSNFormattingOptions
  ): ExtendedFormattingResult {
    const opts = { ...this.defaultOptions, ...options } as SSNFormattingOptions;
    
    if (!opts.maskAfterBlur) {
      // If masking is disabled, return full formatted SSN
      return this.format(fullSSN, options, undefined, false);
    }

    const digits = this.stripFormatting(fullSSN);
    
    if (digits.length < 4) {
      // Can't mask if less than 4 digits
      return this.format(fullSSN, options, undefined, false);
    }

    // Store original value securely if element provided
    if (element) {
      this.originalValues.set(element, digits);
    }

    // Create masked format: XXX-XX-1234
    const last4 = digits.substring(digits.length - 4);
    const maskChar = opts.maskCharacter || this.maskCharacter;
    
    let maskedValue = '';
    if (digits.length >= 9) {
      // Full masked format
      maskedValue = `${maskChar}${maskChar}${maskChar}-${maskChar}${maskChar}-${last4}`;
    } else if (digits.length >= 5) {
      // Partial masked format
      const visiblePart = digits.substring(5);
      maskedValue = `${maskChar}${maskChar}${maskChar}-${maskChar}${maskChar}-${visiblePart}`;
    } else {
      // Too short to mask meaningfully
      maskedValue = this.format(fullSSN, options, undefined, false).formattedValue;
    }

    // Store masked value reference if element provided
    if (element) {
      this.maskedValues.set(element, maskedValue);
    }

    return {
      formattedValue: maskedValue,
      rawValue: last4, // Only return last 4 for security
      success: true,
      errors: [],
      wasModified: true
    };
  }

  /**
   * Remove masking and restore full SSN (for editing)
   */
  public removeMasking(element: HTMLElement): string | null {
    const originalValue = this.originalValues.get(element);
    if (originalValue) {
      // Return formatted full SSN
      const result = this.format(originalValue, {}, undefined, false);
      return result.formattedValue;
    }
    return null;
  }

  /**
   * Validate SSN
   */
  protected performValidation(rawValue: string, options?: FormattingOptions): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for minimum length
    if (rawValue.length < 9) {
      errors.push('SSN must be 9 digits');
    }

    // Check for maximum length
    if (rawValue.length > 9) {
      errors.push('SSN cannot exceed 9 digits');
    }

    // Check for valid SSN patterns (basic validation)
    if (rawValue.length === 9) {
      // SSN cannot be all zeros
      if (rawValue === '000000000') {
        errors.push('Invalid SSN format');
      }

      // Area number (first 3 digits) cannot be 000, 666, or 900-999
      const area = rawValue.substring(0, 3);
      if (area === '000' || area === '666') {
        errors.push('Invalid SSN area number');
      }
      
      const areaNum = parseInt(area);
      if (areaNum >= 900 && areaNum <= 999) {
        errors.push('Invalid SSN area number');
      }

      // Group number (middle 2 digits) cannot be 00
      const group = rawValue.substring(3, 5);
      if (group === '00') {
        errors.push('Invalid SSN group number');
      }

      // Serial number (last 4 digits) cannot be 0000
      const serial = rawValue.substring(5, 9);
      if (serial === '0000') {
        errors.push('Invalid SSN serial number');
      }

      // Check for obviously fake patterns
      const invalidPatterns = [
        '123456789',
        '111111111',
        '222222222', 
        '333333333',
        '444444444',
        '555555555',
        '777777777',
        '888888888',
        '999999999'
      ];

      if (invalidPatterns.includes(rawValue)) {
        warnings.push('SSN appears to be invalid or test data');
      }
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Check if input is a complete SSN
   */
  protected isCompleteInput(rawValue: string): boolean {
    return rawValue.length === 9;
  }

  /**
   * Check if character is valid for SSN input (numeric only)
   */
  protected isValidInputCharacter(char: string): boolean {
    return /\d/.test(char);
  }

  /**
   * Remove formatting to get raw digits
   */
  protected stripFormatting(value: string): string {
    // Remove dashes and mask characters, keep only digits
    return value.replace(/[^\d]/g, '');
  }

  /**
   * Check if a value appears to be masked
   */
  private isMaskedValue(value: string): boolean {
    return value.includes(this.maskCharacter) || value.includes('X');
  }

  /**
   * Extract last 4 digits from masked SSN
   */
  private extractLast4FromMasked(maskedValue: string): string {
    // Extract digits from the end of the masked value
    const digits = maskedValue.replace(/[^\d]/g, '');
    return digits.substring(Math.max(0, digits.length - 4));
  }

  /**
   * Calculate cursor position after SSN formatting
   */
  private calculateSSNCursorPosition(
    originalRaw: string,
    newRaw: string,
    formatted: string,
    originalCursor: number
  ): number {
    // Similar to phone number cursor calculation
    let digitCount = 0;
    for (let i = 0; i < formatted.length; i++) {
      const char = formatted[i];
      if (/\d/.test(char)) {
        digitCount++;
        if (digitCount === originalCursor + 1) {
          return i + 1;
        }
      }
    }

    // Default: place cursor at end
    return formatted.length;
  }

  /**
   * Clear stored values for an element (for cleanup)
   */
  public clearStoredValues(element: HTMLElement): void {
    this.originalValues.delete(element);
    this.maskedValues.delete(element);
  }

  /**
   * Check if element currently has masked value
   */
  public isMasked(element: HTMLElement): boolean {
    return this.maskedValues.has(element);
  }

  /**
   * Format SSN for display purposes
   * 
   * @param ssnDigits Raw SSN digits
   * @returns Formatted SSN string
   */
  public static formatSSNForDisplay(ssnDigits: string): string {
    const formatter = new SSNFormatter();
    const result = formatter.format(ssnDigits, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Create masked SSN for display
   * 
   * @param ssnDigits Full SSN digits
   * @returns Masked SSN string (XXX-XX-1234)
   */
  public static createMaskedSSN(ssnDigits: string): string {
    const formatter = new SSNFormatter();
    const result = formatter.applyMasking(ssnDigits);
    return result.formattedValue;
  }

  /**
   * Check if a string looks like an SSN
   * 
   * @param value String to check
   * @returns Whether it looks like an SSN
   */
  public static looksLikeSSN(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 9;
  }

  /**
   * Parse SSN from various formats
   * 
   * @param input SSN in any format
   * @returns Clean digit string
   */
  public static parseSSN(input: string): string {
    // Remove dashes, spaces, and any non-numeric characters
    return input.replace(/\D/g, '');
  }

  /**
   * Validate SSN format without creating formatter instance
   * 
   * @param ssn SSN to validate
   * @returns Whether SSN is valid
   */
  public static isValidSSN(ssn: string): boolean {
    const digits = ssn.replace(/\D/g, '');
    
    if (digits.length !== 9) return false;
    
    // Basic validation rules
    if (digits === '000000000') return false;
    if (digits.substring(0, 3) === '000') return false;
    if (digits.substring(0, 3) === '666') return false;
    if (digits.substring(3, 5) === '00') return false;
    if (digits.substring(5, 9) === '0000') return false;
    
    const area = parseInt(digits.substring(0, 3));
    if (area >= 900 && area <= 999) return false;
    
    return true;
  }
}